import { AfterViewInit, Component, DestroyRef, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';

import { MainMenuComponent } from '../../shared/main-menu/main-menu.component';
import { ScriptLoaderService, VENDOR_SCRIPTS } from '../../script-loader.service';
import { SubscribeComponent } from '../../shared/subscribe/subscribe.component';
import { ContactRequest } from '../../requests/ContactRequest';
import { BaseResponse } from '../../responses/BaseResponse';
import { ContactService } from '../services/ContactService';
import { AlertService } from '../services/alert-service';
import { Status } from '../../utilities/Status';
@Component({
  selector: 'app-contact',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MainMenuComponent, SubscribeComponent],
  templateUrl: '../templates/contact.component.html'
})
export class ContactComponent implements AfterViewInit, OnDestroy {
  isLoad = false;
  contactUsForm!: FormGroup;
  contactRequest!: ContactRequest;
  contactResponse!: BaseResponse;
  isFormSubmitted = false;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly scriptLoader: ScriptLoaderService,
    private readonly fb: FormBuilder,
    private readonly contactService: ContactService,
    private readonly alertService: AlertService
  ) { }

  ngOnInit() {
    this.contactUsForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.pattern('^[A-Za-z ]+$')]],
      lastName: ['', [Validators.required, Validators.pattern('^[A-Za-z ]+$')]],
      phoneNumber: ['', [Validators.required, Validators.pattern('^[0-9]{10}$')]],
      emailAddress: ['', [Validators.required, Validators.email]],
      message: ['']
    });
  }

  ngAfterViewInit(): void {
    document.body.classList.add('defult-home');
    this.scriptLoader
      .loadSequential(...VENDOR_SCRIPTS)
      .then(() => {
        // If vendor scripts were injected after the native load event,
        // re-dispatch it so jQuery load handlers in main.js still run.
        if (document.readyState === 'complete') {
          window.dispatchEvent(new Event('load'));
        }
      })
      .catch((error) => {
        console.error('Failed to load third-party scripts', error);
      });
  }

  ngOnDestroy(): void {
    document.body.classList.remove('defult-home');
  }

  private resetContactFormState() {
    this.contactUsForm.reset();
    this.contactUsForm.markAsPristine();
    this.contactUsForm.markAsUntouched();
    this.isFormSubmitted = false;
  }

  isFieldValid(field: string) {
    return (
      (!this.contactUsForm.get(field)!.valid && this.contactUsForm.get(field)!.touched) ||
      (this.contactUsForm.get(field)!.untouched && this.isFormSubmitted)
    );
  }

  onSubmit() {
    this.isFormSubmitted = true;
    if (this.contactUsForm.invalid) {
      this.contactUsForm.markAllAsTouched();
      return;
    }

    this.isLoad = true;
    this.contactRequest = {
      FirstName: this.contactUsForm.controls['firstName'].value,
      LastName: this.contactUsForm.controls['lastName'].value,
      ContactNumber: this.contactUsForm.controls['phoneNumber'].value,
      EmailAddress: this.contactUsForm.controls['emailAddress'].value,
      Message: this.contactUsForm.controls['message'].value,
      Website: 'sbes'
    };

    this.contactResponse = new BaseResponse();
    this.contactService.insertContact(this.contactRequest)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          this.contactResponse = data;
          if (this.contactResponse.Status === Status.Yes) {
            this.alertService.showSuccess(this.contactResponse.Message, 'Contact');
            this.resetContactFormState();
          } else {
            this.alertService.showError(this.contactResponse.Message, 'Contact');
          }
          this.isLoad = false;
        },
        error: (error) => {
          const errorMessage = error?.error?.message || error?.message || 'Unable to submit contact details. Please try again.';
          this.alertService.showError(errorMessage, 'Contact');
          this.isLoad = false;
        }
      });
  }
}

